import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { authClient } from '../../lib/auth-client'
import { User, Mail, Key, CheckCircle2, AlertCircle } from 'lucide-react'

export function SettingsPage() {
  const { data: session, refetch } = authClient.useSession()
  const [name, setName] = useState(session?.user?.name || '')
  const [isUpdating, setIsUpdating] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsUpdating(true)
    setMessage(null)

    try {
      const { error } = await authClient.updateUser({
        name,
      })

      if (error) {
        setMessage({ type: 'error', text: error.message || 'Failed to update profile' })
      } else {
        setMessage({ type: 'success', text: 'Profile updated successfully' })
        refetch()
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Something went wrong' })
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>Settings | Twenty</title>
      </Helmet>

      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight mb-2">Settings</h1>
          <p className="text-muted-foreground">
            Manage your account settings
          </p>
        </div>

        {/* Profile */}
        <div className="card-factory p-6">
          <h2 className="text-lg font-medium mb-6 flex items-center gap-2">
            <User className="w-5 h-5" />
            Profile
          </h2>

          {message && (
            <div aria-live="polite" className={`mb-6 p-3 rounded-sm flex items-center gap-2 ${
              message.type === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-600' 
                : 'bg-red-50 border border-red-200 text-red-600'
            }`}>
              {message.type === 'success' ? (
                <CheckCircle2 className="w-4 h-4" />
              ) : (
                <AlertCircle className="w-4 h-4" />
              )}
              {message.text}
            </div>
          )}

          <form onSubmit={handleUpdateProfile} className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-medium mb-2">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-factory"
                placeholder="Your name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <input
                  type="email"
                  value={session?.user?.email || ''}
                  disabled
                  className="input-factory bg-secondary cursor-not-allowed flex-1"
                />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Email cannot be changed
              </p>
            </div>

            <button
              type="submit"
              disabled={isUpdating || name === session?.user?.name}
              className="btn-factory"
            >
              {isUpdating ? 'Saving…' : 'Save changes'}
            </button>
          </form>
        </div>

        {/* API Keys Quick Link */}
        <div className="card-factory p-6">
          <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
            <Key className="w-5 h-5" />
            API Keys
          </h2>
          <p className="text-muted-foreground mb-4">
            Manage your API keys in the API Keys section
          </p>
          <a href="/dashboard/keys" className="btn-factory-outline inline-flex">
            Manage API Keys
          </a>
        </div>

        {/* Account */}
        <div className="card-factory p-6">
          <h2 className="text-lg font-medium mb-4">Account</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-border">
              <div>
                <p className="font-medium">Sign out</p>
                <p className="text-sm text-muted-foreground">Sign out of your account on this device</p>
              </div>
              <button
                type="button"
                onClick={async () => {
                  await authClient.signOut()
                  window.location.href = '/'
                }}
                className="btn-factory-outline text-sm"
              >
                Sign out
              </button>
            </div>

            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-red-600">Delete account</p>
                <p className="text-sm text-muted-foreground">Permanently delete your account and all data</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (confirm('Are you sure? This cannot be undone.')) {
                    // TODO: Implement account deletion
                    alert('Please contact support@twenty.sh to delete your account')
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-sm transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
